import API from '../config/api';
import React, { useState, useEffect } from 'react';

import axios from 'axios';

import { Container, Card, Form, Row, Col } from 'react-bootstrap';

import { toast } from 'react-toastify';

import CustomNavbar from '../components/CustomNavbar';

import { confirmToast } from '../utils/toast';

/* A coupon's state is the whole story of the ticket — it decides the stub
   gradient, the status pill, the hover glow and whether the ticket is dimmed.
   Order matters: switched off beats expired beats fully redeemed. */
const stateOf = (coupon) => {
  const expired = new Date(coupon.expiryDate) < new Date();

  const exhausted =
    Number(coupon.usageLimit) > 0 &&
    Number(coupon.usedCount) >= Number(coupon.usageLimit);

  if (!coupon.isActive) {
    return {
      key: 'off',
      label: 'Inactive',
      icon: '⏸',
      from: '#475569',
      to: '#94a3b8',
      solid: '#64748b'
    };
  }

  if (expired) {
    return {
      key: 'expired',
      label: 'Expired',
      icon: '⌛',
      from: '#991b1b',
      to: '#f87171',
      solid: '#dc2626'
    };
  }

  if (exhausted) {
    return {
      key: 'used',
      label: 'Fully redeemed',
      icon: '🎯',
      from: '#5b21b6',
      to: '#a855f7',
      solid: '#7c3aed'
    };
  }

  return {
    key: 'live',
    label: 'Active',
    icon: '●',
    from: '#c2410c',
    to: '#fbbf24',
    solid: '#ea580c'
  };
};

/* The usage bar warms up as the coupon gets consumed */
const usageTone = (pct) => {
  if (pct >= 90) return 'is-hot';
  if (pct >= 60) return 'is-warm';

  return 'is-cool';
};

function AdminCouponsPage() {
  const [code, setCode] = useState('');

  const [discount, setDiscount] = useState('');

  const [expiryDate, setExpiryDate] = useState('');

  const [usageLimit, setUsageLimit] = useState(100);

  const [coupons, setCoupons] = useState([]);

  /* All products (to scope a coupon) + the ones the admin selects. */
  const [products, setProducts] = useState([]);

  const [selectedProducts, setSelectedProducts] = useState([]);

  /* Filter for the product picker — the list gets long fast */
  const [productSearch, setProductSearch] = useState('');

  /* Announce the new offer to all customers by SMS */
  const [notifyCustomers, setNotifyCustomers] = useState(true);

  useEffect(() => {
    fetchCoupons();
    fetchProducts();
  }, []);

  const fetchCoupons = async () => {
    try {
      const { data } = await axios.get(`${API}/coupons`);

      setCoupons(data);
    } catch (error) {
      console.log(error);
    }
  };

  const fetchProducts = async () => {
    try {
      const { data } = await axios.get(`${API}/products`);

      setProducts(data);
    } catch (error) {
      console.log(error);
    }
  };

  const toggleProduct = (id) => {
    setSelectedProducts((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]
    );
  };
  const toggleStatus = async (id) => {
    try {
      await axios.put(`${API}/coupons/toggle/${id}`);

      fetchCoupons();
    } catch (error) {
      console.log(error);
    }
  };
  const submitHandler = async (e) => {
    e.preventDefault();

    try {
      await axios.post(
        `${API}/coupons`,

        {
          code,

          discountPercentage: Number(discount),

          expiryDate,

          usageLimit: Number(usageLimit),

          products: selectedProducts,

          notifyCustomers
        }
      );

      toast.success('Coupon Created');

      setCode('');
      setDiscount('');
      setExpiryDate('');
      setUsageLimit(100);
      setSelectedProducts([]);
      setProductSearch('');

      fetchCoupons();
    } catch (error) {
      toast.error('Failed To Create Coupon');
    }
  };

  const deleteHandler = async (id) => {
    if (!(await confirmToast('Delete Coupon?'))) {
      return;
    }

    try {
      await axios.delete(`${API}/coupons/${id}`);

      toast.success('Coupon Deleted');

      fetchCoupons();
    } catch (error) {
      toast.error('Delete Failed');
    }
  };

  const visibleProducts = products.filter((p) =>
    p.name?.toLowerCase().includes(productSearch.toLowerCase())
  );

  /* Live headline numbers */
  const liveCount = coupons.filter((c) => stateOf(c).key === 'live').length;
  const expiredCount = coupons.filter((c) => stateOf(c).key === 'expired').length;
  const redemptions = coupons.reduce(
    (sum, c) => sum + (Number(c.usedCount) || 0),
    0
  );

  return (
    <>
      <CustomNavbar />

      <div className="acoup-wrap">
        <Container>
          {/* Header */}
          <Card className="border-0 shadow-lg mb-4 acoup-hero">
            <span className="acoup-hero-aurora" aria-hidden="true" />
            <span className="acoup-hero-blob acoup-hero-blob-1" />
            <span className="acoup-hero-blob acoup-hero-blob-2" />

            {/* Motes drifting up through the banner */}
            <span className="acoup-motes" aria-hidden="true">
              {Array.from({ length: 8 }).map((_, i) => (
                <span className="acoup-mote" key={i} />
              ))}
            </span>

            <Card.Body className="position-relative p-4 p-md-5">
              <h1 className="fw-bold mb-1 acoup-hero-title">
                <span className="acoup-hero-icon">🎟</span>
                <span className="acoup-hero-title-text">Coupon Management</span>
              </h1>

              <p className="mb-0 acoup-hero-text">
                Create and manage discount coupons — {coupons.length} total
              </p>

              <div className="acoup-hero-stats">
                <div className="acoup-hstat">
                  <span className="acoup-hstat-value">{coupons.length}</span>
                  <span className="acoup-hstat-label">Coupons</span>
                </div>

                <span className="acoup-hstat-sep" />

                <div className="acoup-hstat">
                  <span className="acoup-hstat-value is-green">
                    {liveCount}
                  </span>
                  <span className="acoup-hstat-label">Active</span>
                </div>

                <span className="acoup-hstat-sep" />

                <div className="acoup-hstat">
                  <span className="acoup-hstat-value is-red">
                    {expiredCount}
                  </span>
                  <span className="acoup-hstat-label">Expired</span>
                </div>

                <span className="acoup-hstat-sep" />

                <div className="acoup-hstat">
                  <span className="acoup-hstat-value is-violet">
                    {redemptions}
                  </span>
                  <span className="acoup-hstat-label">Redeemed</span>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Create form */}
          <Card className="border-0 shadow-sm mb-5 acoup-form-card overflow-hidden">
            <div className="acoup-form-header d-flex align-items-center gap-3">
              <span className="acoup-form-icon">➕</span>
              <h4 className="mb-0 fw-bold">Create New Coupon</h4>
            </div>

            <Card.Body className="p-4">
              <Form onSubmit={submitHandler}>
                <Row className="g-3 align-items-end">
                  <Col lg={3} md={6}>
                    <Form.Label className="acoup-label">
                      🏷️ Coupon Code
                    </Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="e.g. SAVE20"
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                      required
                    />
                  </Col>

                  <Col lg={3} md={6}>
                    <Form.Label className="acoup-label">
                      💸 Discount %
                    </Form.Label>
                    <Form.Control
                      type="number"
                      placeholder="20"
                      value={discount}
                      onChange={(e) => setDiscount(e.target.value)}
                      required
                    />
                  </Col>

                  <Col lg={3} md={6}>
                    <Form.Label className="acoup-label">
                      📅 Expiry Date
                    </Form.Label>
                    <Form.Control
                      type="date"
                      value={expiryDate}
                      onChange={(e) => setExpiryDate(e.target.value)}
                      required
                    />
                  </Col>

                  <Col lg={3} md={6}>
                    <Form.Label className="acoup-label">
                      🎯 Usage Limit
                    </Form.Label>
                    <Form.Control
                      type="number"
                      placeholder="100"
                      value={usageLimit}
                      onChange={(e) => setUsageLimit(e.target.value)}
                      min="1"
                      required
                    />
                  </Col>
                </Row>

                {/* Live preview of the ticket being built */}
                <div className="acoup-live">
                  <span className="acoup-live-tag">Live preview</span>

                  <div className="acoup-live-ticket">
                    <span className="acoup-live-pct">
                      {discount ? `${discount}%` : '—'}
                    </span>
                    <span className="acoup-live-code">
                      {code || 'YOUR CODE'}
                    </span>
                    <span className="acoup-live-meta">
                      {expiryDate
                        ? `until ${new Date(expiryDate).toLocaleDateString()}`
                        : 'no expiry set'}
                      {' · '}
                      {usageLimit || 0} uses
                    </span>
                  </div>
                </div>

                {/* Product scope — leave empty to apply store-wide */}
                <div className="mt-4">
                  <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                    <Form.Label className="acoup-label mb-0 flex-grow-1">
                      🛍 Apply To Products (optional — leave empty for all
                      products)
                    </Form.Label>

                    {selectedProducts.length > 0 && (
                      <button
                        type="button"
                        className="acoup-mini-btn"
                        onClick={() => setSelectedProducts([])}
                      >
                        ✕ Clear {selectedProducts.length}
                      </button>
                    )}
                  </div>

                  {products.length > 6 && (
                    <div className="acoup-picker-search">
                      <span aria-hidden="true">🔍</span>
                      <input
                        type="text"
                        placeholder="Filter products…"
                        value={productSearch}
                        onChange={(e) => setProductSearch(e.target.value)}
                      />
                    </div>
                  )}

                  <div className="acoup-product-picker">
                    {products.length === 0 ? (
                      <small className="text-muted">Loading products…</small>
                    ) : visibleProducts.length === 0 ? (
                      <small className="text-muted">No products match.</small>
                    ) : (
                      visibleProducts.map((p) => (
                        <label
                          key={p._id}
                          className={
                            'acoup-product-chip' +
                            (selectedProducts.includes(p._id)
                              ? ' selected'
                              : '')
                          }
                        >
                          <input
                            type="checkbox"
                            checked={selectedProducts.includes(p._id)}
                            onChange={() => toggleProduct(p._id)}
                          />
                          <span>{p.name}</span>
                        </label>
                      ))
                    )}
                  </div>

                  <small
                    className={`acoup-scope-note${
                      selectedProducts.length ? ' is-scoped' : ''
                    }`}
                  >
                    {selectedProducts.length === 0
                      ? '🌐 Applies to all products (store-wide)'
                      : `🎯 Applies to ${selectedProducts.length} selected product(s)`}
                  </small>
                </div>

                <div className="mt-3 acoup-notify">
                  <Form.Check
                    type="switch"
                    id="notify-customers-sms"
                    label="📣 Announce this offer to all customers by SMS"
                    checked={notifyCustomers}
                    onChange={(e) => setNotifyCustomers(e.target.checked)}
                  />
                </div>

                <div className="mt-4">
                  <button type="submit" className="acoup-create-btn">
                    <span className="acoup-create-ico" aria-hidden="true">
                      +
                    </span>
                    Create Coupon
                  </button>
                </div>
              </Form>
            </Card.Body>
          </Card>

          {coupons.length === 0 ? (
            <Card
              className="border-0 shadow-sm text-center py-5"
              style={{ borderRadius: '18px' }}
            >
              <Card.Body>
                <div className="acoup-empty-icon">🎟</div>
                <h5 className="fw-bold mt-2">No Coupons Yet</h5>
                <p className="text-muted mb-0">
                  Create your first coupon using the form above.
                </p>
              </Card.Body>
            </Card>
          ) : (
            <Row className="g-4">
              {coupons.map((coupon, index) => {
                const usedPct = coupon.usageLimit
                  ? Math.min(
                      100,
                      Math.round((coupon.usedCount / coupon.usageLimit) * 100)
                    )
                  : 0;

                const state = stateOf(coupon);

                return (
                  <Col xl={4} lg={6} md={6} xs={12} key={coupon._id}>
                    <div
                      className={`acoup-ticket is-${state.key}`}
                      style={{
                        '--t-from': state.from,
                        '--t-to': state.to,
                        '--t-accent': state.solid,
                        /* capped so late tickets don't wait seconds */
                        '--i': Math.min(index, 11)
                      }}
                    >
                      {/* Left stub */}
                      <div className="acoup-ticket-left">
                        <span className="acoup-stub-sheen" aria-hidden="true" />
                        <div className="acoup-ticket-pct">
                          {coupon.discountPercentage}%
                        </div>
                        <small>OFF</small>
                      </div>

                      {/* Body */}
                      <div className="acoup-ticket-body">
                        <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                          <div className="acoup-code">{coupon.code}</div>

                          <span className="acoup-state-pill">
                            <span
                              className="acoup-state-dot"
                              aria-hidden="true"
                            />
                            {state.icon} {state.label}
                          </span>
                        </div>

                        <div className="acoup-meta mb-2 d-flex flex-column">
                          <span
                            className={
                              state.key === 'expired'
                                ? 'acoup-meta-alert'
                                : 'text-muted'
                            }
                          >
                            📅 {state.key === 'expired' ? 'Expired' : 'Expires'}{' '}
                            {new Date(coupon.expiryDate).toLocaleDateString()}
                          </span>
                          <span className="text-muted">
                            {coupon.products && coupon.products.length > 0
                              ? `🎯 ${coupon.products.length} product(s)`
                              : '🌐 All products'}
                          </span>
                        </div>

                        {/* Usage bar */}
                        <div className="acoup-usage mb-1 d-flex justify-content-between">
                          <small className="text-muted">
                            Used {coupon.usedCount} / {coupon.usageLimit}
                          </small>
                          <small className="fw-bold">{usedPct}%</small>
                        </div>
                        <div className="acoup-usage-track mb-3">
                          <div
                            className={`acoup-usage-fill ${usageTone(usedPct)}`}
                            style={{ width: `${usedPct}%` }}
                          />
                        </div>

                        <div className="d-flex gap-2">
                          <button
                            type="button"
                            className={`acoup-act flex-fill ${
                              coupon.isActive
                                ? 'acoup-act-pause'
                                : 'acoup-act-play'
                            }`}
                            onClick={() => toggleStatus(coupon._id)}
                          >
                            <span className="acoup-act-ico" aria-hidden="true">
                              {coupon.isActive ? '⏸' : '▶'}
                            </span>
                            {coupon.isActive ? 'Deactivate' : 'Activate'}
                          </button>

                          <button
                            type="button"
                            className="acoup-act acoup-act-del flex-fill"
                            onClick={() => deleteHandler(coupon._id)}
                          >
                            <span className="acoup-act-ico" aria-hidden="true">
                              🗑
                            </span>
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  </Col>
                );
              })}
            </Row>
          )}
        </Container>
      </div>

      <style>{couponsStyles}</style>
    </>
  );
}

const couponsStyles = `
  .acoup-wrap {
    background: radial-gradient(720px 320px at 8% -5%, rgba(124,58,237,0.12), transparent 70%), radial-gradient(720px 320px at 92% 105%, rgba(234,88,12,0.14), transparent 70%), linear-gradient(180deg, #faf5ff 0%, #fff7ed 55%, #fef3c7 100%);
    min-height: 100vh;
    padding: clamp(24px, 4vw, 40px) 0;
  }

  /* ===== Sanctum banner ===== */
  .acoup-hero {
    position: relative;
    overflow: hidden;
    isolation: isolate;
    border-radius: 25px;
    color: #fff;
    background: linear-gradient(120deg, #0f172a, #3b0764 30%, #7c2d12 64%, #ea580c);
    background-size: 220% 220%;
    border: 1px solid rgba(253, 230, 138, 0.25) !important;
    animation: acoupHeroDrift 18s ease-in-out infinite alternate;
  }
  @keyframes acoupHeroDrift {
    from { background-position: 0% 50%; }
    to   { background-position: 100% 50%; }
  }
  .acoup-hero::before,
  .acoup-hero::after {
    content: '';
    position: absolute;
    left: 0; right: 0;
    height: 2px;
    z-index: 3;
    background: linear-gradient(90deg, transparent, #fde68a 20%, #f59e0b 50%, #fde68a 80%, transparent);
    background-size: 220% 100%;
    animation: acoupThread 3.6s linear infinite;
  }
  .acoup-hero::before { top: 0; }
  .acoup-hero::after { bottom: 0; animation-direction: reverse; }
  @keyframes acoupThread {
    to { background-position: 220% 0; }
  }

  .acoup-hero-aurora {
    position: absolute;
    inset: -40%;
    z-index: 0;
    pointer-events: none;
    background:
      radial-gradient(36% 44% at 24% 30%, rgba(251,191,36,0.40), transparent 62%),
      radial-gradient(32% 40% at 78% 26%, rgba(168,85,247,0.34), transparent 62%),
      radial-gradient(40% 46% at 58% 84%, rgba(234,88,12,0.38), transparent 62%);
    filter: blur(30px);
    animation: acoupAurora 22s ease-in-out infinite alternate;
    will-change: transform;
  }
  @keyframes acoupAurora {
    0%   { transform: translate3d(-4%, -3%, 0) rotate(0deg)  scale(1.05); }
    50%  { transform: translate3d(5%, 4%, 0)   rotate(8deg)  scale(1.18); }
    100% { transform: translate3d(-3%, 5%, 0)  rotate(-6deg) scale(1.1); }
  }

  .acoup-hero-blob {
    position: absolute;
    border-radius: 50%;
    filter: blur(60px);
    opacity: 0.45;
    pointer-events: none;
    z-index: 0;
  }
  .acoup-hero-blob-1 {
    width: 200px; height: 200px;
    background: #f59e0b;
    top: -80px; left: 18%;
  }
  /* was a duplicate of blob-1's amber — violet gives the pair some depth */
  .acoup-hero-blob-2 {
    width: 180px; height: 180px;
    background: #a855f7;
    bottom: -90px; right: 8%;
    opacity: 0.35;
  }

  .acoup-motes { position: absolute; inset: 0; z-index: 1; pointer-events: none; overflow: hidden; }
  .acoup-mote {
    position: absolute;
    bottom: -12px;
    width: 6px; height: 6px;
    border-radius: 50%;
    background: rgba(253, 230, 138, 0.9);
    box-shadow: 0 0 12px rgba(251, 191, 36, 0.9);
    opacity: 0;
    animation: acoupMote 12s linear infinite;
  }
  .acoup-mote:nth-child(1) { left: 7%;  animation-delay: 0s; }
  .acoup-mote:nth-child(2) { left: 20%; animation-delay: 4.3s; width: 4px; height: 4px; }
  .acoup-mote:nth-child(3) { left: 33%; animation-delay: 1.7s; }
  .acoup-mote:nth-child(4) { left: 46%; animation-delay: 7.2s; width: 5px; height: 5px; }
  .acoup-mote:nth-child(5) { left: 59%; animation-delay: 2.8s; }
  .acoup-mote:nth-child(6) { left: 72%; animation-delay: 9.2s; width: 4px; height: 4px; }
  .acoup-mote:nth-child(7) { left: 85%; animation-delay: 5.6s; }
  .acoup-mote:nth-child(8) { left: 95%; animation-delay: 3.4s; width: 5px; height: 5px; }
  @keyframes acoupMote {
    0%   { opacity: 0; transform: translateY(0) translateX(0) scale(0.6); }
    12%  { opacity: 1; }
    80%  { opacity: 0.6; }
    100% { opacity: 0; transform: translateY(-220px) translateX(22px) scale(1.1); }
  }

  .acoup-hero .card-body { z-index: 2; }
  .acoup-hero-title { display: flex; align-items: center; gap: 10px; }
  .acoup-hero-icon {
    display: inline-block;
    animation: acoupIconBob 3.2s ease-in-out infinite;
  }
  @keyframes acoupIconBob {
    0%, 100% { transform: translateY(0) rotate(0deg); }
    50%      { transform: translateY(-6px) rotate(-8deg); }
  }
  .acoup-hero-title-text {
    background: linear-gradient(100deg,
      #ffffff 0%, #ffffff 32%, #fde68a 46%, #fbbf24 52%, #fde68a 58%, #ffffff 74%, #ffffff 100%);
    background-size: 250% 100%;
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    color: transparent;
    animation: acoupTitleShine 5.5s ease-in-out infinite;
  }
  @keyframes acoupTitleShine {
    0%, 100% { background-position: 130% 0; }
    50%      { background-position: -30% 0; }
  }
  .acoup-hero-text { opacity: 0.85; }

  .acoup-hero-stats {
    display: inline-flex;
    align-items: center;
    flex-wrap: wrap;
    gap: clamp(14px, 3vw, 26px);
    margin-top: 20px;
    padding: 13px clamp(18px, 3vw, 26px);
    border-radius: 20px;
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(253, 230, 138, 0.3);
    backdrop-filter: blur(8px);
  }
  .acoup-hstat { line-height: 1.15; }
  .acoup-hstat-value {
    display: block;
    font-size: clamp(1.05rem, 3vw, 1.4rem);
    font-weight: 800;
    color: #fde68a;
  }
  .acoup-hstat-value.is-green  { color: #6ee7b7; }
  .acoup-hstat-value.is-red    { color: #fca5a5; }
  .acoup-hstat-value.is-violet { color: #d8b4fe; }
  .acoup-hstat-label {
    display: block;
    font-size: 0.63rem;
    text-transform: uppercase;
    letter-spacing: 1px;
    opacity: 0.75;
    margin-top: 3px;
  }
  .acoup-hstat-sep {
    width: 1px;
    height: 28px;
    background: linear-gradient(180deg, transparent, rgba(253,230,138,0.5), transparent);
  }

  /* ===== Form ===== */
  .acoup-form-card {
    border-radius: 20px;
    border: 1px solid rgba(22, 163, 74, 0.16) !important;
  }
  .acoup-form-header {
    position: relative;
    overflow: hidden;
    background: linear-gradient(120deg, #064e3b, #16a34a 55%, #0d9488);
    background-size: 220% 100%;
    color: #fff;
    padding: 16px 24px;
    animation: acoupHeaderDrift 14s ease-in-out infinite alternate;
  }
  @keyframes acoupHeaderDrift {
    from { background-position: 0% 50%; }
    to   { background-position: 100% 50%; }
  }
  .acoup-form-header::after {
    content: '';
    position: absolute;
    left: 0; right: 0; bottom: 0;
    height: 2px;
    background: linear-gradient(90deg, transparent, #bbf7d0 20%, #4ade80 50%, #bbf7d0 80%, transparent);
    background-size: 220% 100%;
    animation: acoupThread 3.2s linear infinite;
  }
  .acoup-form-icon {
    width: 44px;
    height: 44px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 13px;
    background: rgba(255,255,255,0.2);
    border: 1px solid rgba(187, 247, 208, 0.45);
    font-size: 1.3rem;
    animation: acoupIconBob 3.4s ease-in-out infinite;
  }
  .acoup-label {
    font-size: 0.78rem;
    font-weight: 800;
    color: #15803d;
    letter-spacing: 0.3px;
    margin-bottom: 5px;
  }
  .acoup-form-card .form-control {
    border-radius: 12px;
    padding: 11px 14px;
    border: 1px solid #e3e9e3;
    background: #fdfffd;
    transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
  }
  .acoup-form-card .form-control:focus {
    background: #fff;
    border-color: #16a34a;
    box-shadow: 0 0 0 0.2rem rgba(22,163,74,0.15);
  }

  /* Live preview of the coupon being typed */
  .acoup-live {
    display: flex;
    align-items: center;
    gap: 14px;
    flex-wrap: wrap;
    margin-top: 20px;
    padding: 14px 16px;
    border-radius: 16px;
    background: linear-gradient(120deg, #fffdf7, #fff7ea);
    border: 1.5px dashed #fcd34d;
  }
  .acoup-live-tag {
    font-size: 0.66rem;
    font-weight: 800;
    letter-spacing: 1px;
    text-transform: uppercase;
    color: #b45309;
  }
  .acoup-live-ticket {
    display: inline-flex;
    align-items: baseline;
    gap: 12px;
    flex-wrap: wrap;
    padding: 9px 18px;
    border-radius: 30px;
    color: #fff;
    background: linear-gradient(120deg, #c2410c, #f59e0b 55%, #fbbf24);
    background-size: 220% 100%;
    box-shadow: 0 8px 22px rgba(234, 88, 12, 0.35);
    animation: acoupLiveFlow 5s ease-in-out infinite alternate;
  }
  @keyframes acoupLiveFlow {
    from { background-position: 0% 50%; }
    to   { background-position: 100% 50%; }
  }
  .acoup-live-pct { font-size: 1.3rem; font-weight: 800; }
  .acoup-live-code {
    font-weight: 800;
    letter-spacing: 1.5px;
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  }
  .acoup-live-meta { font-size: 0.72rem; opacity: 0.85; }

  .acoup-mini-btn {
    border: 1px solid #fecaca;
    border-radius: 30px;
    padding: 3px 12px;
    font-size: 0.72rem;
    font-weight: 700;
    color: #b91c1c;
    background: #fff;
    cursor: pointer;
    transition: background 0.2s ease, color 0.2s ease, transform 0.2s ease;
  }
  .acoup-mini-btn:hover {
    background: #fef2f2;
    transform: translateY(-1px);
  }

  .acoup-picker-search {
    display: flex;
    align-items: center;
    gap: 9px;
    margin-bottom: 8px;
    padding: 8px 14px;
    border-radius: 30px;
    background: #fff;
    border: 1px solid #e3e9e3;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
  }
  .acoup-picker-search:focus-within {
    border-color: #16a34a;
    box-shadow: 0 0 0 0.2rem rgba(22,163,74,0.12);
  }
  .acoup-picker-search input {
    flex: 1 1 auto;
    border: none;
    outline: none;
    background: transparent;
    font-size: 0.88rem;
  }

  .acoup-scope-note {
    display: inline-block;
    margin-top: 10px;
    padding: 4px 13px;
    border-radius: 30px;
    font-size: 0.76rem;
    font-weight: 700;
    color: #475569;
    background: #f1f5f9;
    border: 1px solid #e2e8f0;
    transition: background 0.25s ease, color 0.25s ease, border-color 0.25s ease;
  }
  .acoup-scope-note.is-scoped {
    color: #15803d;
    background: #f0fdf4;
    border-color: #bbf7d0;
  }

  .acoup-notify .form-check-input:checked {
    background-color: #16a34a;
    border-color: #16a34a;
  }

  .acoup-create-btn {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    border: none;
    border-radius: 12px;
    padding: 12px 32px;
    font-weight: 800;
    color: #fff;
    cursor: pointer;
    background: linear-gradient(135deg, #065f46, #16a34a 55%, #0d9488);
    box-shadow: 0 10px 24px rgba(22, 163, 74, 0.38);
    transition: transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1),
      box-shadow 0.25s ease, filter 0.25s ease;
  }
  .acoup-create-btn:hover {
    transform: translateY(-3px);
    filter: brightness(1.08);
    box-shadow: 0 16px 32px rgba(22, 163, 74, 0.5);
  }
  .acoup-create-btn:active { transform: translateY(-1px) scale(0.97); }
  .acoup-create-ico {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 21px; height: 21px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.24);
    font-size: 1.05rem;
    line-height: 1;
    transition: transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
  }
  .acoup-create-btn:hover .acoup-create-ico { transform: rotate(180deg) scale(1.15); }

  /* ===== Coupon tickets ===== */
  .acoup-ticket {
    position: relative;
    display: flex;
    background: #fff;
    border-radius: 16px;
    overflow: hidden;
    height: 100%;
    box-shadow: 0 6px 18px rgba(15, 23, 42, 0.08);
    transition: transform 0.25s ease, box-shadow 0.25s ease;
    animation: acoupTicketIn 0.5s cubic-bezier(0.22, 1, 0.36, 1) backwards;
    animation-delay: calc(var(--i, 0) * 60ms);
  }
  @keyframes acoupTicketIn {
    from { opacity: 0; transform: translateY(20px) scale(0.96); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }
  .acoup-ticket:hover {
    transform: translateY(-6px);
    box-shadow: 0 22px 40px color-mix(in srgb, var(--t-accent, #ea580c) 30%, transparent);
  }
  @supports not (box-shadow: 0 0 0 color-mix(in srgb, red 10%, blue)) {
    .acoup-ticket:hover { box-shadow: 0 20px 38px rgba(234, 88, 12, 0.2); }
  }
  /* Anything not currently usable reads back, not forward */
  .acoup-ticket.is-off,
  .acoup-ticket.is-expired,
  .acoup-ticket.is-used { opacity: 0.82; }

  .acoup-ticket-left {
    position: relative;
    flex-shrink: 0;
    width: 92px;
    overflow: hidden;
    background: linear-gradient(135deg, var(--t-from, #ea580c), var(--t-to, #f59e0b));
    color: #fff;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }
  /* Light sweeping down the stub — only while the coupon is usable */
  .acoup-stub-sheen {
    position: absolute;
    left: -40%; right: -40%;
    height: 45%;
    top: -60%;
    background: linear-gradient(180deg, transparent, rgba(255,255,255,0.45), transparent);
    transform: rotate(-18deg);
    pointer-events: none;
  }
  .acoup-ticket.is-live .acoup-stub-sheen {
    animation: acoupSheen 3.4s ease-in-out infinite;
  }
  @keyframes acoupSheen {
    0%, 60% { top: -60%; }
    100%    { top: 130%; }
  }
  /* Perforation — white punches read as holes against the coloured stub */
  .acoup-ticket-left::after {
    content: "";
    position: absolute;
    right: -8px;
    top: 0; bottom: 0;
    width: 16px;
    background-image: radial-gradient(circle at center, #ffffff 5px, transparent 6px);
    background-size: 16px 18px;
    background-repeat: repeat-y;
  }
  .acoup-ticket-pct {
    position: relative;
    font-size: 1.7rem;
    font-weight: 800;
    line-height: 1;
    text-shadow: 0 2px 8px rgba(0,0,0,0.2);
  }
  .acoup-ticket-left small {
    position: relative;
    font-weight: 700;
    letter-spacing: 1px;
  }
  .acoup-ticket-body {
    flex: 1;
    padding: 16px 18px;
    min-width: 0;
  }
  .acoup-code {
    font-size: 1.2rem;
    font-weight: 800;
    letter-spacing: 1px;
    color: #1e293b;
    word-break: break-word;
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  }

  /* Status pill, tinted by the ticket's own state */
  .acoup-state-pill {
    flex: none;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 3px 12px;
    border-radius: 30px;
    font-size: 0.7rem;
    font-weight: 700;
    white-space: nowrap;
    color: #fff;
    background: var(--t-accent, #ea580c);
  }
  .acoup-state-dot {
    width: 6px; height: 6px;
    border-radius: 50%;
    background: #fff;
  }
  /* Only a live coupon has a heartbeat */
  .acoup-ticket.is-live .acoup-state-dot {
    animation: acoupDot 1.7s ease-in-out infinite;
  }
  @keyframes acoupDot {
    0%, 100% { opacity: 0.4; transform: scale(0.8); }
    50%      { opacity: 1;   transform: scale(1.35); }
  }

  .acoup-meta { font-size: 0.85rem; }
  .acoup-meta-alert { color: #dc2626; font-weight: 700; }

  .acoup-usage-track {
    height: 9px;
    border-radius: 10px;
    background: #eef0f3;
    overflow: hidden;
    box-shadow: inset 0 1px 3px rgba(15, 23, 42, 0.1);
  }
  .acoup-usage-fill {
    height: 100%;
    border-radius: 10px;
    transition: width 0.5s cubic-bezier(0.22, 1, 0.36, 1), background 0.4s ease;
  }
  /* The bar warms as the coupon is consumed */
  .acoup-usage-fill.is-cool { background: linear-gradient(90deg, #059669, #34d399); }
  .acoup-usage-fill.is-warm { background: linear-gradient(90deg, #d97706, #fbbf24); }
  .acoup-usage-fill.is-hot  { background: linear-gradient(90deg, #b91c1c, #f87171); }
  .acoup-usage-fill.is-hot {
    animation: acoupHot 1.8s ease-in-out infinite;
  }
  @keyframes acoupHot {
    0%, 100% { box-shadow: 0 0 0 0 rgba(248, 113, 113, 0.6); }
    50%      { box-shadow: 0 0 8px 2px rgba(248, 113, 113, 0.35); }
  }

  /* ===== Ticket actions ===== */
  .acoup-act {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    border-radius: 12px;
    padding: 8px 12px;
    font-size: 0.82rem;
    font-weight: 700;
    cursor: pointer;
    background: #fff;
    transition: transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1),
      box-shadow 0.25s ease, background 0.25s ease, color 0.25s ease,
      border-color 0.25s ease;
  }
  .acoup-act:hover { transform: translateY(-2px); }
  .acoup-act:active { transform: translateY(0) scale(0.96); }
  .acoup-act-ico { display: inline-block; }

  .acoup-act-pause {
    border: 1px solid #fcd34d;
    color: #b45309;
  }
  .acoup-act-pause:hover {
    color: #431407;
    border-color: transparent;
    background: linear-gradient(135deg, #fde68a, #fbbf24 55%, #f59e0b);
    box-shadow: 0 10px 22px rgba(245, 158, 11, 0.42);
  }

  .acoup-act-play {
    border: 1px solid #86efac;
    color: #15803d;
  }
  .acoup-act-play:hover {
    color: #fff;
    border-color: transparent;
    background: linear-gradient(135deg, #065f46, #16a34a 55%, #4ade80);
    box-shadow: 0 10px 22px rgba(22, 163, 74, 0.42);
  }
  .acoup-act-play:hover .acoup-act-ico {
    animation: acoupNudge 0.7s ease-in-out infinite;
  }
  @keyframes acoupNudge {
    0%, 100% { transform: translateX(0); }
    50%      { transform: translateX(3px); }
  }

  .acoup-act-del {
    border: 1px solid #fecaca;
    color: #b91c1c;
  }
  .acoup-act-del:hover {
    color: #fff;
    border-color: transparent;
    background: linear-gradient(135deg, #991b1b, #dc2626 55%, #ef4444);
    box-shadow: 0 10px 22px rgba(220, 38, 38, 0.42);
  }
  .acoup-act-del:hover .acoup-act-ico {
    animation: acoupShake 0.55s ease-in-out infinite;
  }
  @keyframes acoupShake {
    0%, 100% { transform: translateX(0) rotate(0deg); }
    30%      { transform: translateX(-2px) rotate(-14deg); }
    65%      { transform: translateX(2px) rotate(11deg); }
  }

  /* ===== Product scope picker ===== */
  .acoup-product-picker {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    max-height: 170px;
    overflow-y: auto;
    padding: 12px;
    border: 1px solid #e3e9e3;
    border-radius: 14px;
    background: #fbfdfb;
  }
  .acoup-product-picker::-webkit-scrollbar { width: 6px; }
  .acoup-product-picker::-webkit-scrollbar-thumb {
    border-radius: 6px;
    background: linear-gradient(180deg, #4ade80, #15803d);
  }
  .acoup-product-chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin: 0;
    padding: 6px 12px;
    border-radius: 30px;
    border: 1px solid #e5e7eb;
    background: #fff;
    font-size: 0.82rem;
    cursor: pointer;
    transition: background 0.2s ease, border-color 0.2s ease, color 0.2s ease,
      transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.2s ease;
  }
  .acoup-product-chip input { cursor: pointer; }
  .acoup-product-chip:hover {
    border-color: #16a34a;
    transform: translateY(-2px);
  }
  .acoup-product-chip.selected {
    background: linear-gradient(135deg, #16a34a, #0d9488);
    border-color: transparent;
    color: #fff;
    box-shadow: 0 6px 14px rgba(22, 163, 74, 0.32);
  }

  .acoup-empty-icon {
    font-size: 3rem;
    animation: acoupIconBob 3s ease-in-out infinite;
  }

  @media (prefers-reduced-motion: reduce) {
    .acoup-hero,
    .acoup-hero::before,
    .acoup-hero::after,
    .acoup-hero-aurora,
    .acoup-mote,
    .acoup-hero-icon,
    .acoup-hero-title-text,
    .acoup-form-header,
    .acoup-form-header::after,
    .acoup-form-icon,
    .acoup-live-ticket,
    .acoup-ticket,
    .acoup-ticket.is-live .acoup-stub-sheen,
    .acoup-ticket.is-live .acoup-state-dot,
    .acoup-usage-fill.is-hot,
    .acoup-act-play:hover .acoup-act-ico,
    .acoup-act-del:hover .acoup-act-ico,
    .acoup-empty-icon { animation: none !important; }
    .acoup-motes, .acoup-stub-sheen { display: none; }
    .acoup-hero-title-text {
      -webkit-text-fill-color: #fff;
      color: #fff;
    }
  }
`;

export default AdminCouponsPage;
